import { useGetEventConfig } from 'api/events/events.api'
import React, { ReactElement, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import BarCalendar from 'ui/components/BarCalendar/BarCalendar'
import EventPostViewModal from '../EventPostViewModal/EventPostViewModal'
import {
  EVENT_TYPES,
  EventPostListItem,
  EventStatus,
  getColorForEventStatus,
  getLabelForEventStatus,
} from '../events.helper'

export type CalendarEvent = {
  id: string
  attribute: string
  type: string
  startTime: ISODateTime
  endTime: ISODateTime
  categoryId: string
  name: string
  meta: {
    tooltipElement: string
    clickable: boolean
    value: string
  }
  tooltipHeader: string
  color: string
}

type EventsCalendarProps = {
  events: EventPostListItem[]
  datePeriod: { startTime: ISODateTime; endTime: ISODateTime }
  initDatePeriod?: 'productionPlanPeriod' | 'week' | 'month' | 'custom'
  setDatePeriod?: (startTime: ISODateTime, endTime: ISODateTime) => void
}

export const transformEventToCalendarEvent = (
  events: EventPostListItem[],
  t: (key: string) => string
): CalendarEvent[] => {
  return events.map((event) => {
    const eventType = EVENT_TYPES.find((e) => e.value === event.operational_event_type)
    return {
      id: event?.id ? event.id.toString() : '',
      attribute: event?.setting_instructions?.linked_objects?.[0]?.attribute || '',
      type: `${event.name || ''}.${event.id || ''}`,
      startTime: event.start_time,
      endTime: event.end_time,
      categoryId: event?.category_id ? event.category_id.toString() : '',
      name: event.name || '',
      require_umm: event.require_umm,
      meta: {
        tooltipElement: eventType ? t(eventType.label) : '',
        clickable: true,
        value: getLabelForEventStatus(event.status as EventStatus),
      },
      tooltipHeader: event.name || '',
      color: getColorForEventStatus(event.status as EventStatus, event.pending_change_request_id || null),
    }
  })
}

export default function EventsCalendar({ events, datePeriod }: EventsCalendarProps): ReactElement {
  const { t } = useTranslation()
  const { systemId } = useAuth()

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedEventPost, setSelectedEventPost] = useState<EventPostListItem | undefined>(undefined)

  const handleRowClick = (row: { selectedEvent: EventPostListItem }): void => {
    setSelectedEventPost(undefined)
    setSelectedEventPost(row.selectedEvent)
    setModalOpen(true)
  }

  const eventConfig = useGetEventConfig(systemId).data
  const eventCategories = eventConfig?.categories || []

  const calendarCategories = eventCategories.map((category) => ({
    id: category.id?.toString() || '',
    name: t(category.name as string),
  }))

  const calendarEvents = useMemo(() => transformEventToCalendarEvent(events, t), [events, t])

  return (
    <>
      <BarCalendar
        startTime={datePeriod.startTime}
        endTime={datePeriod.endTime}
        events={calendarEvents}
        categories={calendarCategories}
        onClick={(event) => {
          handleRowClick({
            selectedEvent: events.find((e) => e.id === parseInt(event.id.toString())) as EventPostListItem,
          })
        }}
        header={t('Cause category')}
        isOperationalEventCalendar
      />
      {modalOpen && (
        <EventPostViewModal closeModal={() => setModalOpen(false)} selectedEventPostId={selectedEventPost?.id} />
      )}
    </>
  )
}
